import React, { useId } from 'react';

import styles from './OfflineMode.module.scss';
import { EVENT_LOG, EVENT_LOG_TYPE_LABELS } from './copy';
import { formatClockTime } from './formatting';
import { SkeletonBlock, SkeletonRegion } from './Skeleton';
import { currentActivationLog, hasBroadcastSince, integrityFindings } from './eventLogReading';
import { useServerDiagnostics } from './serverDiagnostics';
import type {
  OfflineModeEvent,
  OfflineModeEventChain,
  OfflineModeEventLogError,
  OfflineModeEventLogStatus,
  OfflineModeEventRetention,
} from 'redux-stores/offlineMode';

export type EventLogPanelProps = {
  status: OfflineModeEventLogStatus;
  events: OfflineModeEvent[];
  chains: OfflineModeEventChain[];
  error: OfflineModeEventLogError | null;
  retention: OfflineModeEventRetention | null;
  unreadable: number;
  isFullyFetched: boolean;
  isActive: boolean;
  activeSince: number;
  onRetry: () => void;
  onShowMore: () => void;
};

const entryProseDigest = (events: OfflineModeEvent[]): string =>
  events
    .map((event) =>
      [event.seq, event.actor?.token, event.detail.reason, event.detail.note]
        .filter(Boolean)
        .join(' ')
    )
    .filter((entry) => entry.includes(' '))
    .join(' | ');

function EntryRow({ event }: { event: OfflineModeEvent }) {
  const label = EVENT_LOG_TYPE_LABELS[event.eventType] || EVENT_LOG.unknownType(event.eventType);
  const occurred = formatClockTime(event.occurredAt);

  return (
    <li className={styles.event} data-test-id={`offlineModeEvent-${event.seq}`}>
      {occurred ? (
        <span className={styles.eventTime} data-test-id={`offlineModeEventTime-${event.seq}`}>
          {occurred}
        </span>
      ) : (
        <span className={styles.eventTime} aria-hidden="true" />
      )}
      <span
        className={styles.eventSentence}
        data-test-id={`offlineModeEventSentence-${event.seq}`}
        data-event-type={event.eventType}
      >
        {EVENT_LOG.sentence(label, event.actor?.name)}
      </span>
    </li>
  );
}

function EventLogGathering() {
  return (
    <SkeletonRegion label={EVENT_LOG.gathering.label} testId="offlineModeEventLogSkeleton">
      {[0, 1, 2].map((row) => (
        <div className={styles.event} key={row}>
          <SkeletonBlock height="14px" width="46%" />
          <SkeletonBlock height="10px" width="70%" />
          <SkeletonBlock height="10px" width="32%" />
        </div>
      ))}
    </SkeletonRegion>
  );
}

export default function EventLogPanel({
  activeSince,
  chains,
  error,
  events,
  isActive,
  isFullyFetched,
  onRetry,
  onShowMore,
  retention,
  status,
  unreadable,
}: EventLogPanelProps) {
  useServerDiagnostics('eventLog', {
    errorCode: error?.code,
    serverMessage: error?.serverMessage,
    retentionStatus: retention?.status,
    retentionNote: retention?.note,
    retentionConfigKey: retention?.configKey,
    entryProse: entryProseDigest(events),
  });
  const headingId = useId();

  const wrap = (children: React.ReactNode) => (
    <section
      aria-labelledby={headingId}
      className={styles.eventLogSection}
      data-test-id="offlineModeEventLog"
      tabIndex={0}
    >
      <h2
        className={styles.sectionHeading}
        data-test-id="offlineModeEventLogHeading"
        id={headingId}
      >
        {isActive ? EVENT_LOG.heading : EVENT_LOG.endedHeading}
      </h2>
      <div className={styles.eventLog}>{children}</div>
    </section>
  );

  if (error?.code === 'forbidden') {
    return wrap(
      <div className={styles.eventNotPermitted} data-test-id="offlineModeEventLogNotPermitted">
        <span className={styles.eventStateHeading}>{EVENT_LOG.notPermitted.heading}</span>
        <p className={styles.body}>{EVENT_LOG.notPermitted.body}</p>
        <p className={styles.body}>{EVENT_LOG.notPermitted.reassurance}</p>
        <p className={styles.hint}>{EVENT_LOG.notPermitted.ask}</p>
      </div>
    );
  }

  if ((status === 'idle' || status === 'loading') && !events.length) {
    return wrap(<EventLogGathering />);
  }

  if (status === 'error' && !events.length) {
    const code = error?.code ?? 'unusable';
    return wrap(
      <div className={styles.eventFailure} data-test-id="offlineModeEventLogFailure" role="alert">
        <span className={styles.eventStateHeading}>{EVENT_LOG.failure.heading}</span>
        <p className={styles.body}>
          {EVENT_LOG.failure.reasons[code] || EVENT_LOG.failure.reasons.unusable}
        </p>
        <button
          className={styles.secondaryAction}
          data-test-id="offlineModeEventLogRetry"
          onClick={onRetry}
          type="button"
        >
          {EVENT_LOG.failure.retryLabel}
        </button>
      </div>
    );
  }

  const scoped = currentActivationLog(events);
  const rows = scoped.events;
  const isScopeComplete = scoped.reachesActivation;
  const notNotified = isActive && !hasBroadcastSince(rows, activeSince);
  const integrity = integrityFindings(chains);

  return wrap(
    <>
      {integrity ? (
        <div
          className={styles.eventFailure}
          data-test-id="offlineModeEventLogIntegrity"
          role="alert"
        >
          <span className={styles.eventStateHeading}>{EVENT_LOG.integrity.heading}</span>
          {integrity.map((line) => (
            <p className={styles.body} key={line}>
              {line}
            </p>
          ))}
        </div>
      ) : null}

      {rows.length ? (
        <ul className={styles.eventList} data-test-id="offlineModeEventList">
          {rows.map((event) => (
            <EntryRow event={event} key={event.eventId || `seq-${event.seq}`} />
          ))}
        </ul>
      ) : null}

      {notNotified ? (
        <p className={styles.eventNotNotified} data-test-id="offlineModeEventLogNotNotified">
          {EVENT_LOG.notNotified}
        </p>
      ) : null}

      {unreadable > 0 ? (
        <p
          className={styles.eventUnreadable}
          data-test-id="offlineModeEventLogUnreadable"
          role="note"
        >
          {EVENT_LOG.failure.unreadableEntries(unreadable)}
        </p>
      ) : null}

      {status === 'error' && events.length ? (
        <div
          className={styles.eventFailure}
          data-test-id="offlineModeEventLogMoreFailure"
          role="alert"
        >
          <span className={styles.eventStateHeading}>{EVENT_LOG.failure.heading}</span>
          <p className={styles.body}>
            {EVENT_LOG.failure.reasons[error?.code ?? 'unusable'] ||
              EVENT_LOG.failure.reasons.unusable}
          </p>
          <button
            className={styles.secondaryAction}
            data-test-id="offlineModeEventLogMoreRetry"
            onClick={onShowMore}
            type="button"
          >
            {EVENT_LOG.failure.retryLabel}
          </button>
        </div>
      ) : null}

      {!isFullyFetched && status !== 'error' && !isScopeComplete ? (
        <button
          className={styles.secondaryAction}
          data-test-id="offlineModeEventLogMore"
          disabled={status === 'loading-more'}
          onClick={onShowMore}
          type="button"
        >
          {status === 'loading-more' ? EVENT_LOG.more.inFlightLabel : EVENT_LOG.more.label}
        </button>
      ) : null}

      {isFullyFetched && rows.length && !isScopeComplete ? (
        <p className={styles.hint} data-test-id="offlineModeEventLogEnd">
          {EVENT_LOG.more.end}
        </p>
      ) : null}
    </>
  );
}
